// Form Builder Service Layer
// This service provides API integration for form builder operations

import axiosInstance from '../utils/axios';

// ===== FORM CATEGORIES OPERATIONS =====

/**
 * Get all form categories from the API
 * @returns {Promise<Object>} - Form categories data
 */
export const getFormCategories = async () => {
  try {
    const response = await axiosInstance.get('/api/FormDesk/GetFormCategory');

    // Parse the stringified JSON data from the API response
    let categoriesData = [];
    if (response.data?.data) {
      try {
        categoriesData = JSON.parse(response.data.data);
      } catch (parseError) {
        console.error('Error parsing form categories data:', parseError);
        // Fallback to empty array if parsing fails
        categoriesData = [];
      }
    }

    // Transform to the format expected by the UI
    const formCategories = categoriesData.map(category => ({
      id: category.Id,
      name: category.CategoryName?.trim() || 'Unnamed Category',
      value: category.Id,
      label: category.CategoryName?.trim() || 'Unnamed Category'
    }));

    return {
      success: true,
      data: formCategories,
      message: response.data?.message || 'Form categories fetched successfully',
      totalRecords: response.data?.totalRecords || formCategories.length
    };
  } catch (error) {
    console.error('Error fetching form categories:', error);

    return {
      success: false,
      data: [],
      message: error.message || 'Failed to fetch form categories',
      error: true
    };
  }
};

// ===== FIELD TYPES OPERATIONS =====

/**
 * Get all available field types from the API
 * @returns {Promise<Object>} - Field types data
 */
export const getFieldTypes = async () => {
  try {
    const response = await axiosInstance.get('/api/FormDesk/GetFieldTypes');

    // Parse the stringified JSON data from the API response
    let fieldTypesData = [];
    if (response.data?.data) {
      try {
        fieldTypesData = JSON.parse(response.data.data);
      } catch (parseError) {
        console.error('Error parsing field types data:', parseError);
        // Fallback to empty array if parsing fails
        fieldTypesData = [];
      }
    }

    // Filter only active field types and transform to the format expected by the UI
    const activeFieldTypes = fieldTypesData
      .filter(fieldType => fieldType.IsActive)
      .map(fieldType => ({
        value: fieldType.HtmlInputType,
        label: fieldType.TypeName,
        icon: getFieldTypeIcon(fieldType.HtmlInputType),
        id: fieldType.FieldTypeId,
        typeName: fieldType.TypeName
      }));

    return {
      success: true,
      data: activeFieldTypes,
      message: response.data?.message || 'Field types fetched successfully',
      totalRecords: response.data?.totalRecords || activeFieldTypes.length
    };
  } catch (error) {
    console.error('Error fetching field types:', error);

    // Return fallback field types if API fails
    return {
      success: false,
      data: getFallbackFieldTypes(),
      message: error.message || 'Failed to fetch field types',
      error: true
    };
  }
};

/**
 * Get icon for field type based on HTML input type
 * @param {string} htmlInputType - HTML input type
 * @returns {string} - Icon emoji
 */
const getFieldTypeIcon = (htmlInputType) => {
  const iconMap = {
    text: "📝",
    textarea: "📄",
    number: "🔢",
    date: "📅",
    checkbox: "☑️",
    radio: "🔘",
    select: "📋",
    email: "📧",
    password: "🔐",
    tel: "📞",
    url: "🌐",
    time: "⏰",
    file: "📎"
  };

  return iconMap[htmlInputType] || "📝";
};

/**
 * Get fallback field types if API fails
 * @returns {Array} - Default field types
 */
const getFallbackFieldTypes = () => [
  { value: "text", label: "Text Input", icon: "📝" },
  { value: "email", label: "Email", icon: "📧" },
  { value: "tel", label: "Phone Number", icon: "📞" },
  { value: "number", label: "Number", icon: "🔢" },
  { value: "date", label: "Date", icon: "📅" },
  { value: "select", label: "Dropdown", icon: "📋" },
  { value: "textarea", label: "Text Area", icon: "📄" },
  { value: "checkbox", label: "Checkbox Group", icon: "☑️" },
  { value: "radio", label: "Radio Buttons", icon: "🔘" },
  { value: "file", label: "File Upload", icon: "📎" },
  { value: "url", label: "Website URL", icon: "🌐" },
  { value: "time", label: "Time", icon: "⏰" }
];

// ===== MASTER FIELD OPERATIONS =====

/**
 * Create or update a master field
 * @param {Object} fieldData - Master field data
 * @returns {Promise<Object>} - API response
 */
export const upsertMasterField = async (fieldData) => {
  try {
    const response = await axiosInstance.post('/api/FormDesk/UpsertMasterField', fieldData);

    // Check if the response indicates an error
    if (response.data?.status === 'Error' || response.data?.statusCode >= 400) {
      return {
        success: false,
        message: response.data?.message || 'Failed to save master field',
        error: true
      };
    }

    return {
      success: true,
      data: response.data,
      message: response.data?.message || 'Master field saved successfully'
    };
  } catch (error) {
    console.error('Error upserting master field:', error);

    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to save master field',
      error: true
    };
  }
};
/**
 * Delete a master field
 * @param {string} fieldId - Master field ID
 * @returns {Promise<Object>} - API response
 */
export const deleteMasterField = async (fieldId) => {
  try {
    const response = await axiosInstance.delete(`/api/FormDesk/DeleteMasterFieldById/${fieldId}`);

    // Check if the response indicates an error
    if (response.data?.status === 'Error' || response.data?.statusCode >= 400) {
      return {
        success: false,
        message: response.data?.message || 'Failed to delete master field',
        error: true
      };
    }

    return {
      success: true,
      data: response.data,
      message: response.data?.message || 'Master field deleted successfully'
    };
  } catch (error) {
    console.error('Error deleting master field:', error);

    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to delete master field',
      error: true
    };
  }
};

/**
 * Map field type HTML input type to fieldTypeId
 * @param {string} htmlInputType - HTML input type (e.g., 'text', 'email')
 * @param {Array} fieldTypes - Available field types from API
 * @returns {number} - Field type ID
 */
const getFieldTypeId = (htmlInputType, fieldTypes = []) => {
  const fieldType = fieldTypes.find(ft => ft.value === htmlInputType);
  const fieldTypeId = fieldType?.id || 1; // Default to 1 if not found


  return fieldTypeId;
};



/**
 * Transform form configuration to API format
 * @param {Object} formConfig - Form configuration from UI
 * @param {boolean} isMultistep - Whether form is multistep
 * @param {Array} fieldTypes - Available field types from API
 * @param {string} formCategoryId - Selected form category ID
 * @param {boolean} isUpdate - Whether this is an update operation (includes IDs)
 * @param {string} formStatus - Form status ('active' or 'inactive')
 * @param {boolean} wasMultistep - Whether the form was previously multistep (for format change detection)
 * @returns {Object} - API formatted form data
 */
const transformFormDataForAPI = (formConfig, isMultistep, fieldTypes = [], formCategoryId = null, isUpdate = false, formStatus = 'active', wasMultistep = null) => {
  // Convert styling object to string
  const styleString = formConfig.styling ? JSON.stringify(formConfig.styling) : JSON.stringify({
    primaryColor: "#3b82f6",
    backgroundColor: "#ffffff",
    textColor: "#1f2937",
    borderRadius: "8px"
  });

  // Transform websites to backend format (comma-separated string)
  const transformWebsites = (websites) => {
    if (!websites) return '';

    // If it's already an array from edit mode
    if (Array.isArray(websites)) {
      if (websites.length === 0) return '';
      return websites
        .map(w => typeof w === 'object' ? w.Website : w)
        .filter(w => w && w.trim())
        .join(',');
    }

    // If it's a comma-separated string from form input
    if (typeof websites === 'string') {
      return websites.trim();
    }

    return '';
  };

  // Detect if form structure is changing (single ↔ multi)
  const isFormatChanging = isUpdate && wasMultistep !== null && wasMultistep !== isMultistep;


  // Process websites and only include if not empty
  const processedWebsites = transformWebsites(formConfig.websites);

  const baseFormData = {
    title: isMultistep ? (formConfig.title || '') : (formConfig.name || ''),
    description: formConfig.description || '',
    isActive: formStatus === 'active',
    formCategoryId: formCategoryId || "F776E99F-6C19-42DF-A490-60F4FA84844F",
    styles: styleString, // Include styles field
    // Only include websites field if it has content
    ...(processedWebsites && processedWebsites.trim() && { websites: processedWebsites }),
    // Include IDs only for update operations
    ...(isUpdate && formConfig.formId && { formId: formConfig.formId }),
    ...(isUpdate && formConfig.companyId && { companyId: formConfig.companyId })
  };

  if (isMultistep && formConfig.steps) {
    // Multistep form
    baseFormData.steps = formConfig.steps.map((step, stepIndex) => ({
      // Include stepId only for update operations and only if it's a real database ID (not temporary frontend ID)
      ...(isUpdate && step.stepId !== undefined && step.stepId > 0 && step.stepId < 1000000000000 && { stepId: step.stepId }),
      ...(isUpdate && formConfig.formId && { formId: formConfig.formId }),
      stepTitle: step.stepTitle || `Step ${stepIndex + 1}`,
      stepDescription: step.stepDescription || '',
      stepOrder: stepIndex,
      fields: (step.fields || []).map((field, fieldIndex) => ({
        // Include fieldId and stepId only for update operations and only if they're real database IDs (not temporary frontend IDs)
        // For new fields (fieldId is temporary), don't include fieldId or stepId - let backend infer from JSON structure
        // IMPORTANT: Don't include fieldId when form structure is changing (single ↔ multi) to avoid FK constraint errors
        ...(isUpdate && !isFormatChanging && field.fieldId !== undefined && field.fieldId > 0 && field.fieldId < 1000000000000 && {
          fieldId: field.fieldId,
          // Include stepId only when fieldId is also included AND stepId is a real database ID
          ...(step.stepId !== undefined && step.stepId > 0 && step.stepId < 1000000000000 && { stepId: step.stepId })
        }),
        fieldLabel: field.fieldLabel || field.label || '',
        fieldTypeId: (() => {
          const existingFieldTypeId = field.fieldTypeId;
          const mappedFieldTypeId = getFieldTypeId(field.fieldType || field.type, fieldTypes);
          const finalFieldTypeId = existingFieldTypeId || mappedFieldTypeId;

          const isNewField = field.fieldId === undefined || field.fieldId <= 0 || field.fieldId >= 1000000000000;

          return finalFieldTypeId;
        })(),
        ...(field.masterFieldId ? { masterFieldId: field.masterFieldId } : {}), // Include masterFieldId only if it exists and is truthy
        isRequired: field.isRequired !== undefined ? field.isRequired : (field.required || false),
        placeholder: field.placeholder || '',
        defaultValue: field.defaultValue || '',
        fieldOrder: fieldIndex,
        options: (field.options || []).map((option, optionIndex) => {
          if (typeof option === 'string') {
            return {
              // Include IDs only for update operations and only if parent field has real database ID
              ...(isUpdate && !isFormatChanging && field.fieldId !== undefined && field.fieldId > 0 && field.fieldId < 1000000000000 && { optionId: 0 }),
              ...(isUpdate && !isFormatChanging && field.fieldId !== undefined && field.fieldId > 0 && field.fieldId < 1000000000000 && { fieldId: field.fieldId }),
              optionLabel: option,
              optionValue: option.toLowerCase().replace(/\s+/g, '_'),
              optionOrder: optionIndex
            };
          } else {
            return {
              // Include IDs only for update operations and only if they're real database IDs (not temporary frontend IDs)
              ...(isUpdate && !isFormatChanging && option.optionId !== undefined && option.optionId > 0 && option.optionId < 1000000000000 && { optionId: option.optionId }),
              ...(isUpdate && field.fieldId !== undefined && field.fieldId > 0 && field.fieldId < 1000000000000 && { fieldId: field.fieldId }),
              optionLabel: option.optionLabel || option.label || '',
              optionValue: option.optionValue || option.value || '',
              optionOrder: optionIndex
            };
          }
        })
      }))
    }));
  } else {
    // Single-step form - convert to multistep format with one step
    // For single-step forms, get the stepId from the first field (they all have the same stepId)
    const singleStepId = formConfig.fields && formConfig.fields.length > 0
      ? formConfig.fields[0].stepId
      : 0;


    baseFormData.steps = [{
      // Include stepId only for update operations and only if it's a real database ID (not temporary frontend ID)
      ...(isUpdate && singleStepId !== undefined && singleStepId > 0 && singleStepId < 1000000000000 && { stepId: singleStepId }),
      stepTitle: "Form Fields",
      stepDescription: "Complete all required fields",
      stepOrder: 0,
      fields: (formConfig.fields || []).map((field, fieldIndex) => ({
        // Include fieldId and stepId only for update operations and only if they're real database IDs (not temporary frontend IDs)
        // For new fields (fieldId is temporary), don't include fieldId or stepId - let backend infer from JSON structure
        // IMPORTANT: Don't include fieldId when form structure is changing (single ↔ multi) to avoid FK constraint errors
        ...(isUpdate && !isFormatChanging && field.fieldId !== undefined && field.fieldId > 0 && field.fieldId < 1000000000000 && {
          fieldId: field.fieldId,
          // Include stepId only when fieldId is also included AND stepId is a real database ID
          ...((field.stepId || singleStepId) !== undefined && (field.stepId || singleStepId) > 0 && (field.stepId || singleStepId) < 1000000000000 && { stepId: field.stepId || singleStepId })
        }),
        fieldLabel: field.fieldLabel || field.label || '',
        fieldTypeId: (() => {
          const existingFieldTypeId = field.fieldTypeId;
          const mappedFieldTypeId = getFieldTypeId(field.fieldType || field.type, fieldTypes);
          const finalFieldTypeId = existingFieldTypeId || mappedFieldTypeId;

          const isNewField = field.fieldId === undefined || field.fieldId <= 0 || field.fieldId >= 1000000000000;

          return finalFieldTypeId;
        })(),
        ...(field.masterFieldId ? { masterFieldId: field.masterFieldId } : {}), // Include masterFieldId only if it exists and is truthy
        isRequired: field.isRequired !== undefined ? field.isRequired : (field.required || false),
        placeholder: field.placeholder || '',
        defaultValue: field.defaultValue || '',
        fieldOrder: fieldIndex,
        options: (field.options || []).map((option, optionIndex) => {
          if (typeof option === 'string') {
            return {
              // Include IDs only for update operations and only if parent field has real database ID
              ...(isUpdate && !isFormatChanging && field.fieldId !== undefined && field.fieldId > 0 && field.fieldId < 1000000000000 && { optionId: 0 }),
              ...(isUpdate && !isFormatChanging && field.fieldId !== undefined && field.fieldId > 0 && field.fieldId < 1000000000000 && { fieldId: field.fieldId }),
              optionLabel: option,
              optionValue: option.toLowerCase().replace(/\s+/g, '_'),
              optionOrder: optionIndex
            };
          } else {
            return {
              // Include IDs only for update operations and only if they're real database IDs (not temporary frontend IDs)
              ...(isUpdate && !isFormatChanging && option.optionId !== undefined && option.optionId > 0 && option.optionId < 1000000000000 && { optionId: option.optionId }),
              ...(isUpdate && field.fieldId !== undefined && field.fieldId > 0 && field.fieldId < 1000000000000 && { fieldId: field.fieldId }),
              optionLabel: option.optionLabel || option.label || '',
              optionValue: option.optionValue || option.value || '',
              optionOrder: optionIndex
            };
          }
        })
      }))
    }];
  }

  return baseFormData;
};

/**
 * Save a new form using the SaveForm API
 * @param {Object} formConfig - Form configuration from UI
 * @param {boolean} isMultistep - Whether form is multistep
 * @param {Array} fieldTypes - Available field types from API
 * @param {string} formCategoryId - Selected form category ID
 * @param {string} formStatus - Form status ('active' or 'inactive')
 * @returns {Promise<Object>} - Response from API
 */
export const saveForm = async (formConfig, isMultistep = false, fieldTypes = [], formCategoryId = null, formStatus = 'active') => {
  try {
    // Transform form data to match API schema (no format change for new forms)
    const apiFormData = transformFormDataForAPI(formConfig, isMultistep, fieldTypes, formCategoryId, false, formStatus, null);


    const response = await axiosInstance.post('/api/FormDesk/SaveForm', apiFormData);

    // Check API response status
    if (response.data?.statusCode === 200 && response.data?.status === 'Success') {
      return {
        success: true,
        data: response.data?.data,
        message: response.data?.message || 'Form saved successfully'
      };
    } else {
      // API returned a failure status
      return {
        success: false,
        message: response.data?.message || 'Failed to save form',
        error: true
      };
    }
  } catch (error) {
    console.error('Error saving form:', error);

    return {
      success: false,
      message: error.message || 'Failed to save form',
      error: true
    };
  }
};

/**
 * Transform backend form data to form builder format for editing
 * @param {Object} apiFormData - Form data from API
 * @param {Array} fieldTypes - Array of field types for mapping
 * @returns {Object} Form configuration object compatible with form builder
 */
export const transformAPIFormToBuilderFormat = (apiFormData, fieldTypes = []) => {
  try {
    // Create fieldType mapping from ID to input type
    const fieldTypeMap = {};
    fieldTypes.forEach(type => {
      fieldTypeMap[type.id] = type.value; // e.g., 1 -> 'text'
    });

    // Helper to get field type from ID
    const getFieldType = (fieldTypeId) => fieldTypeMap[fieldTypeId] || 'text';

    // Parse style if it's a string - check both 'Styles' and 'Style' for backward compatibility
    let styling = {
      primaryColor: "#3B82F6",
      backgroundColor: "#FFFFFF",
      textColor: "#1F2937",
      borderRadius: "8px",
      fontFamily: "Inter, sans-serif"
    };

    const styleData = apiFormData.Styles || apiFormData.Style;
    if (styleData) {
      try {
        styling = { ...styling, ...JSON.parse(styleData) };
      } catch (e) {
        console.warn('Failed to parse form style:', e);
      }
    }

    // Transform form data
    const formConfig = {
      name: apiFormData.Title || '',
      title: apiFormData.Title || '',
      description: apiFormData.Description || '',
      websites: apiFormData.Websites || [], // Include websites from API (array format)
      styling: styling,
      formId: apiFormData.FormId,
      companyId: apiFormData.CompanyId, // Include companyId if available
      isActive: apiFormData.IsActive !== undefined ? apiFormData.IsActive : true, // Include active status from API
      formCategoryId: apiFormData.FormCategoryId || null // Include form category ID for edit mode
    };

    // Check if it's a multi-step form
    const isMultistep = apiFormData.Steps && apiFormData.Steps.length > 1;

    if (isMultistep) {
      // Multi-step form
      formConfig.steps = apiFormData.Steps.map(step => {
        return {
          stepId: step.StepId,
          stepTitle: step.StepTitle,
          stepDescription: step.StepDescription,
          stepOrder: step.StepOrder,
          fields: step.Fields ? step.Fields.map(field => ({
            fieldId: field.FieldId,
            id: field.FieldId,
            stepId: step.StepId, // Add stepId to field from parent step
            fieldLabel: field.FieldLabel,
            label: field.FieldLabel,
            fieldType: getFieldType(field.FieldTypeId),
            type: getFieldType(field.FieldTypeId),
            isRequired: field.IsRequired,
            required: field.IsRequired,
            placeholder: field.Placeholder || '',
            defaultValue: field.DefaultValue || '',
            fieldOrder: field.FieldOrder,
            IsMendatory: field.IsMendatory || false, // Preserve IsMendatory from API
            isMandatory: field.IsMendatory || false, // Also set lowercase version for consistency
            ...(field.MasterFieldId && {
              masterFieldId: field.MasterFieldId,
              isMasterField: true,
              isEditable: false // Master fields should not be editable
            }),
            options: field.Options ? field.Options.map(option => ({
              optionId: option.OptionId,
              optionLabel: option.OptionLabel,
              label: option.OptionLabel,
              optionValue: option.OptionValue,
              value: option.OptionValue,
              optionOrder: option.OptionOrder,
              order: option.OptionOrder
            })) : []
          })) : []
        };
      });
      formConfig.fields = []; // Empty for multi-step
    } else {
      // Single-step form
      const firstStep = apiFormData.Steps && apiFormData.Steps[0];
      formConfig.fields = firstStep && firstStep.Fields ? firstStep.Fields.map(field => ({
        fieldId: field.FieldId,
        id: field.FieldId,
        stepId: firstStep.StepId, // Add stepId to field from parent step
        fieldLabel: field.FieldLabel,
        label: field.FieldLabel,
        fieldType: getFieldType(field.FieldTypeId),
        type: getFieldType(field.FieldTypeId),
        isRequired: field.IsRequired,
        required: field.IsRequired,
        placeholder: field.Placeholder || '',
        defaultValue: field.DefaultValue || '',
        fieldOrder: field.FieldOrder,
        IsMendatory: field.IsMendatory || false, // Preserve IsMendatory from API
        isMandatory: field.IsMendatory || false, // Also set lowercase version for consistency
        ...(field.MasterFieldId && {
          masterFieldId: field.MasterFieldId,
          isMasterField: true,
          isEditable: false // Master fields should not be editable
        }),
        options: field.Options ? field.Options.map(option => ({
          optionId: option.OptionId,
          optionLabel: option.OptionLabel,
          label: option.OptionLabel,
          optionValue: option.OptionValue,
          value: option.OptionValue,
          optionOrder: option.OptionOrder,
          order: option.OptionOrder
        })) : []
      })) : [];
      formConfig.steps = []; // Empty for single-step
    }

    return {
      formConfig,
      isMultistep
    };
  } catch (error) {
    console.error('Error transforming API form to builder format:', error);
    throw error;
  }
};

/**
 * Update an existing form in the backend
 * @param {Object} formConfig - The form configuration object
 * @param {boolean} isMultistep - Whether the form is multistep
 * @param {Array} fieldTypes - Array of field types for mapping
 * @param {number} formId - The ID of the form to update
 * @param {string} formCategoryId - Selected form category ID
 * @param {string} formStatus - Form status ('active' or 'inactive')
 * @param {boolean} wasMultistep - Whether the form was previously multistep (for format change detection)
 * @returns {Promise<Object>} Response object with success status and data
 */
export const updateForm = async (formConfig, isMultistep = false, fieldTypes = [], formId, formCategoryId = null, formStatus = 'active', wasMultistep = null) => {
  try {
    // Transform form data to match API schema (with isUpdate = true) 
    const apiFormData = transformFormDataForAPI(formConfig, isMultistep, fieldTypes, formCategoryId, true, formStatus, wasMultistep);

    // Set the formId for update (camelCase as backend expects)
    apiFormData.formId = formId;


    // Send data directly to UpdateForm endpoint (no wrapper needed)
    const response = await axiosInstance.post('/api/FormDesk/UpdateForm', apiFormData);
    
    // Check API response status
    if (response.data?.statusCode === 200 && response.data?.status === 'Success') {
      return {
        success: true,
        data: response.data?.data,
        message: response.data?.message || 'Form updated successfully'
      };
    } else {
      // API returned a failure status
      return {
        success: false,
        message: response.data?.message || 'Failed to update form',
        error: true
      };
    }
  } catch (error) {
    console.error('Error updating form:', error);

    return {
      success: false,
      message: error.message || 'Failed to update form',
      error: true
    };
  }
};

/**
 * Create a new form (legacy - now uses saveForm)
 * @param {Object} formData - Form configuration data
 * @returns {Promise<Object>} - Response from API
 * @deprecated Use saveForm instead
 */
export const createForm = async (formData) => {
  console.warn('createForm is deprecated, use saveForm instead');
  try {
    const response = await axiosInstance.post('/api/FormDesk/CreateForm', formData);

    return {
      success: true,
      data: response.data?.data,
      message: response.data?.message || 'Form created successfully'
    };
  } catch (error) {
    console.error('Error creating form:', error);

    return {
      success: false,
      message: error.message || 'Failed to create form',
      error: true
    };
  }
};



/**
 * Get form by ID with full details including steps and fields
 * @param {number} formId - Form ID
 * @returns {Promise<Object>} - Form data with steps and fields
 */
export const getFormById = async (formId) => {
  try {
    const response = await axiosInstance.get(`/api/FormDesk/${formId}`);

    let formData = null;
    if (response.data?.data) {
      try {
        // Parse if it's stringified JSON
        formData = typeof response.data.data === 'string'
          ? JSON.parse(response.data.data)
          : response.data.data;
      } catch (parseError) {
        console.error('Error parsing form data:', parseError);
        formData = response.data.data;
      }
    }

    return {
      success: true,
      data: formData,
      message: response.data?.message || 'Form fetched successfully'
    };
  } catch (error) {
    console.error('Error fetching form:', error);

    return {
      success: false,
      message: error.message || 'Failed to fetch form',
      error: true
    };
  }
};

/**
 * Get all forms using the GetAllForms API
 * @param {Object} params - Query parameters
 * @param {string} params.companyId - Company ID
 * @param {number} params.pageNumber - Page number (0-based)
 * @param {number} params.pageSize - Page size (0 for all)
 * @param {string} params.search - Search term
 * @returns {Promise<Object>} - Forms data
 */
export const getAllForms = async (params = {}) => {
  try {
    const requestData = {
      companyId: params.companyId || "3fa85f64-5717-4562-b3fc-2c963f66afa6", // Dummy company ID
      pageNumber: params.pageNumber || 1,
      pageSize: params.pageSize || 0, // 0 for all forms
      search: params.search || "",
      ...(params.formCategoryId && { formCategoryId: params.formCategoryId })
    };


    const response = await axiosInstance.post('/api/FormDesk/GetAllForms', requestData);

    // Parse the stringified JSON data from the API response
    let formsData = [];
    if (response.data?.data) {
      try {
        formsData = JSON.parse(response.data.data);
      } catch (parseError) {
        console.error('Error parsing forms data:', parseError);
        // Fallback to empty array if parsing fails
        formsData = [];
      }
    }

    return {
      success: true,
      data: formsData,
      message: response.data?.message || 'Forms fetched successfully',
      totalRecords: response.data?.totalRecords || formsData.length
    };
  } catch (error) {
    console.error('Error fetching forms:', error);

    return {
      success: false,
      data: [],
      message: error.message || 'Failed to fetch forms',
      totalRecords: 0,
      error: true
    };
  }
};

/**
 * Get all form templates using the GetAllTemplates API
 * @returns {Promise<Object>} - Templates data
 */
export const getAllTemplates = async () => {
  try {

    const response = await axiosInstance.get('/api/FormDesk/GetAllTemplates');

    // Parse the stringified JSON data from the API response
    let templatesData = [];
    if (response.data?.data) {
      try {
        templatesData = JSON.parse(response.data.data);
      } catch (parseError) {
        console.error('Error parsing templates data:', parseError);
        // Fallback to empty array if parsing fails
        templatesData = [];
      }
    }


    return {
      success: true,
      data: templatesData,
      message: response.data?.message || 'Templates fetched successfully',
      totalRecords: response.data?.totalRecords || templatesData.length
    };
  } catch (error) {
    console.error('Error fetching templates:', error);

    return {
      success: false,
      data: [],
      message: error.message || 'Failed to fetch templates',
      totalRecords: 0,
      error: true
    };
  }
};

/**
 * Get full template data with steps and fields
 * @param {string} templateId - Template ID (GUID)
 * @returns {Promise<Object>} - Template data with full details
 */
export const getTemplateWithStepsAndFields = async (templateId) => {
  try {

    const response = await axiosInstance.get(`/api/FormDesk/GetTemplateWithStepsAndFields?TemplateId=${templateId}`);

    // Parse the stringified JSON data from the API response
    let templateData = null;
    if (response.data?.data) {
      try {
        const parsedData = JSON.parse(response.data.data);
        // API returns an array with single template
        templateData = Array.isArray(parsedData) ? parsedData[0] : parsedData;
      } catch (parseError) {
        console.error('Error parsing template data:', parseError);
        templateData = null;
      }
    }


    return {
      success: true,
      data: templateData,
      message: response.data?.message || 'Template retrieved successfully',
      totalRecords: response.data?.totalRecords || 1
    };
  } catch (error) {
    console.error('Error fetching template details:', error);

    return {
      success: false,
      data: null,
      message: error.message || 'Failed to fetch template details',
      totalRecords: 0,
      error: true
    };
  }
};

/**
 * Get all forms (legacy - now uses getAllForms)
 * @param {Object} filters - Filter criteria
 * @returns {Promise<Object>} - Forms data
 * @deprecated Use getAllForms instead
 */
export const getForms = async (filters = {}) => {
  console.warn('getForms is deprecated, use getAllForms instead');
  try {
    const queryParams = new URLSearchParams(filters);
    const response = await axiosInstance.get(`/api/FormDesk/GetForms?${queryParams}`);

    return {
      success: true,
      data: response.data?.data,
      message: response.data?.message || 'Forms fetched successfully',
      totalRecords: response.data?.totalRecords || 0
    };
  } catch (error) {
    console.error('Error fetching forms:', error);

    return {
      success: false,
      data: [],
      message: error.message || 'Failed to fetch forms',
      error: true
    };
  }
};

/**
 * Delete a form
 * @param {number} formId - Form ID
 * @returns {Promise<Object>} - Response from API
 */
export const deleteForm = async (formId) => {
  try {

    // Ensure formId is a string (GUID format) and properly encoded
    const formIdString = String(formId).trim();

    // Validate GUID format (basic check)
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!guidRegex.test(formIdString)) {
      console.warn('Invalid GUID format:', formIdString);
      return {
        success: false,
        message: 'Invalid form ID format. Cannot delete form.'
      };
    }

    // URL encode the GUID to handle any special characters
    const encodedFormId = encodeURIComponent(formIdString);

    const response = await axiosInstance.delete(`/api/FormDesk/DeleteFormById/${encodedFormId}`);

    // Check if API returned a failure status in the response body
    if (response.data?.status === "Failure" || response.data?.status === "failure" || 
        (response.data?.statusCode && response.data.statusCode >= 400)) {
      return {
        success: false,
        message: response.data?.message || 'Failed to delete form'
      };
    }

    return {
      success: true,
      message: response.data?.message || 'Form deleted successfully'
    };
  } catch (error) {
    console.error('Error deleting form:', error);

    // Extract error message from API response
    let errorMessage = 'Failed to delete form';
    if (error.response) {
      const serverMessage = error.response.data?.message || error.response.data?.error;
      errorMessage = serverMessage || errorMessage;
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      message: errorMessage,
      error: true
    };
  }
};

/**
 * Submit form data from public form
 * @param {number} formId - Form ID
 * @param {Object} formData - Form submission data
 * @returns {Promise<Object>} - Response from API
 */
export const submitForm = async (formId, formData) => {
  try {
    const response = await axiosInstance.post('/api/FormDesk/SubmitForm', {
      formId,
      submissionData: formData,
      submittedAt: new Date().toISOString()
    });

    return {
      success: true,
      data: response.data?.data,
      message: response.data?.message || 'Form submitted successfully'
    };
  } catch (error) {
    console.error('Error submitting form:', error);

    return {
      success: false,
      message: error.message || 'Failed to submit form',
      error: true
    };
  }
};


export const SubmitFormForLead = async (payload) => {
  try {
    const response = await axiosInstance.post('/api/FormDesk/SubmitFormForLead', payload);

    return {
      success: response.data?.success,
      data: response.data,
      message: response.data?.message || 'Form submitted successfully'
    };
  } catch (error) {
    console.error('Error submitting form:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to submit form',
      error: true
    };
  }
};


/**
 * Update form websites and status using optimized single API call
 * @param {string} formId - The form ID (GUID)
 * @param {string} websiteNames - Comma-separated website names
 * @param {boolean} status - Form status (true for active, false for inactive)
 * @returns {Promise<Object>} Response object with success status and data
 */
export const updateFormWebsitesAndStatus = async (formId, websiteNames, status) => {
  try {

    // Validate that formId is a GUID (basic check)
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const formIdString = String(formId).trim();

    if (!guidRegex.test(formIdString)) {
      throw new Error(`Invalid form ID format: ${formId}`);
    }

    // Prepare payload for the optimized API endpoint
    const payload = {
      formId: formIdString,
      websiteNames: websiteNames || '',
      status: Boolean(status)
    };


    // Call the optimized endpoint
    const response = await axiosInstance.post('/api/FormDesk/UpdateFormWebsitesAndStatus', payload);


    // Check for failure status
    if (response.data?.status === "Failure" || response.data?.statusCode >= 400) {
      throw new Error(response.data?.message || 'Failed to update form');
    }

    return {
      success: true,
      message: response.data.message || 'Form updated successfully',
      data: response.data
    };
  } catch (error) {
    console.error('Error updating form websites and status:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to update form',
      error: error.response?.data || error
    };
  }
};

export const updateFormWebsites = async (formId, websites) => {
  try {

    // Validate that formId is a GUID (basic check)
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const formIdString = String(formId).trim();

    if (!guidRegex.test(formIdString)) {
      throw new Error(`Invalid form ID format: ${formId}`);
    }

    // First, get the current form data
    const currentFormResult = await getFormById(formIdString);
    if (!currentFormResult.success) {
      throw new Error('Failed to fetch current form data');
    }

    const currentForm = currentFormResult.data;

    // Prepare full payload for website update using existing UpdateForm endpoint
    const payload = {
      formId: formIdString,
      title: currentForm.Title,
      description: currentForm.Description || '',
      isActive: currentForm.IsActive,
      formCategoryId: currentForm.FormCategoryId,
      websites: websites || '', // Use the provided websites
      steps: currentForm.Steps || []
    };


    // Use existing UpdateForm endpoint instead of non-existent UpdateFormWebsites
    const response = await axiosInstance.post('/api/FormDesk/UpdateForm', payload);


    return {
      success: true,
      message: response.data.message || 'Websites updated successfully',
      data: response.data
    };
  } catch (error) {
    console.error('Error updating form websites:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to update websites',
      error: error.response?.data || error
    };
  }
};

export const updateFormStatus = async (formId, status) => {
  try {

    // Validate that formId is a GUID (basic check)
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const formIdString = String(formId).trim();

    if (!guidRegex.test(formIdString)) {
      throw new Error(`Invalid form ID format: ${formId}`);
    }

    // Validate status
    const validStatuses = ['active', 'inactive'];
    if (!validStatuses.includes(status)) {
      throw new Error(`Invalid status: ${status}. Must be one of: ${validStatuses.join(', ')}`);
    }

    // First, get the current form data
    const currentFormResult = await getFormById(formIdString);
    if (!currentFormResult.success) {
      throw new Error('Failed to fetch current form data');
    }

    const currentForm = currentFormResult.data;

    // Prepare minimal payload for status update using existing UpdateForm endpoint
    const payload = {
      formId: formIdString,
      title: currentForm.Title,
      description: currentForm.Description || '',
      isActive: status === 'active',
      formCategoryId: currentForm.FormCategoryId,
      websites: Array.isArray(currentForm.Websites)
        ? currentForm.Websites.map(w => typeof w === 'object' ? w.Website : w).filter(w => w).join(',')
        : (currentForm.Websites || ''),
      steps: currentForm.Steps || []
    };


    // Use existing UpdateForm endpoint
    const response = await axiosInstance.post('/api/FormDesk/UpdateForm', payload);


    return {
      success: true,
      message: response.data.message || 'Form status updated successfully',
      data: response.data
    };
  } catch (error) {
    console.error('Error updating form status:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to update form status',
      error: error.response?.data || error
    };
  }
};

/**
 * Webhook API for public form access (used for iframe embeds)
 * Automatically detects parent domain and sends verification headers
 * 
 * Backend Requirements:
 * - Server must validate X-Referrer-Origin header against authorized domains
 * - X-In-Frame header indicates if request is from iframe
 * - X-Current-Origin header shows the current domain
 * 
 * @param {string} formId - The form ID to fetch
 * @returns {Promise<Object>} - Response with form data or error
 */
export const getFormByIdWebhook = async (formId) => {
  try {

    // Validate that formId is a GUID (basic check)
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const formIdString = String(formId).trim();

    if (!guidRegex.test(formIdString)) {
      throw new Error(`Invalid form ID format: ${formId}`);
    }

    // Detect iframe and parent domain information
    const isInFrame = window !== window.parent;
    const currentOrigin = window.location.origin;
    let parentOrigin = currentOrigin;

    // Try to get parent origin if in iframe
    if (isInFrame) {
      try {
        // Try to access parent origin (may fail due to CORS)
        parentOrigin = window.parent.location.origin;
      } catch (e) {
        // If we can't access parent origin due to CORS, use document.referrer
        if (document.referrer) {
          try {
            const referrerUrl = new URL(document.referrer);
            parentOrigin = referrerUrl.origin;
          } catch (urlError) {
            console.warn('Failed to parse referrer URL:', urlError);
            parentOrigin = currentOrigin;
          }
        }
      }
    }

 

    // Prepare headers for server-side domain verification
    const headers = {
      'X-Referrer-Origin': parentOrigin,
      'X-Current-Origin': currentOrigin,
      'X-In-Frame': isInFrame.toString()
    };


    const response = await axiosInstance.get(`/api/WebHook/GetFormById/${encodeURIComponent(formIdString)}`, {
      headers
    });


    // Check if the response indicates unauthorized access
    if (response.data.statusCode === 401 || response.data.message === "Unauthorised") {
      return {
        success: false,
        statusCode: 401,
        message: response.data.message || 'Unauthorized access',
        error: 'unauthorized'
      };
    }

    // Parse the data if it's a JSON string
    let parsedData = response.data.data || response.data;

    if (typeof parsedData === 'string') {
      try {
        parsedData = JSON.parse(parsedData);
      } catch (parseError) {
        console.error('Error parsing form data JSON:', parseError);
        throw new Error('Invalid form data format received from server');
      }
    }

    return {
      success: true,
      data: parsedData,
      message: response.data.message || 'Form fetched successfully'
    };
  } catch (error) {
    console.error('Error fetching form via webhook:', error);

    // Extract status code for better error handling
    const statusCode = error.response?.status || error.response?.statusCode;

    return {
      success: false,
      statusCode: statusCode,
      message: error.response?.data?.message || error.message || 'Failed to fetch form',
      error: error.response?.data || error
    };
  }
};

/**
 * Webhook API for public form submission (used for iframe embeds)
 * Automatically detects parent domain and sends verification headers
 * 
 * @param {string} formId - The form ID to submit to
 * @param {Object} formData - The form submission data
 * @returns {Promise<Object>} - Response with submission result
 */
export const submitFormWebhook = async (formId, formData) => {
  try {

    // Validate that formId is a GUID (basic check)
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const formIdString = String(formId).trim();

    if (!guidRegex.test(formIdString)) {
      throw new Error(`Invalid form ID format: ${formId}`);
    }

    // Detect iframe and parent domain information (same logic as getFormByIdWebhook)
    const isInFrame = window !== window.parent;
    const currentOrigin = window.location.origin;
    let parentOrigin = currentOrigin;

    // Try to get parent origin if in iframe
    if (isInFrame) {
      try {
        // Try to access parent origin (may fail due to CORS)
        parentOrigin = window.parent.location.origin;
      } catch (e) {
        // If we can't access parent origin due to CORS, use document.referrer
        if (document.referrer) {
          try {
            const referrerUrl = new URL(document.referrer);
            parentOrigin = referrerUrl.origin;
          } catch (urlError) {
            console.warn('Failed to parse referrer URL:', urlError);
            parentOrigin = currentOrigin;
          }
        }
      }
    }

    // Prepare headers for server-side domain verification
    const headers = {
      'X-Referrer-Origin': parentOrigin,
      'X-Current-Origin': currentOrigin,
      'X-In-Frame': isInFrame.toString()
    };


    // Transform formData object to fieldSubmissions array format
    const fieldSubmissions = Object.entries(formData).map(([fieldId, value]) => {
      let processedValue;

      if (Array.isArray(value)) {
        // Handle checkbox arrays
        processedValue = value.join(', ');
      } else if (typeof value === 'object' && value !== null) {
        // Check if it's a React SyntheticEvent
        if (value.nativeEvent && value.target && typeof value.preventDefault === 'function') {
          // Extract value from SyntheticEvent
          processedValue = String(value.target.value || '');
          console.warn(`Field ${fieldId} contains SyntheticEvent, extracting value:`, processedValue);
        } else if (value instanceof Element || value instanceof Node) {
          // Extract value from HTML elements
          processedValue = value.value || value.textContent || String(value);
          console.warn(`Field ${fieldId} contains HTML element, extracting value:`, processedValue);
        } else if (Object.prototype.hasOwnProperty.call(value, 'value')) {
          processedValue = String(value.value);
        } else if (Object.prototype.hasOwnProperty.call(value, 'optionValue')) {
          processedValue = String(value.optionValue);
        } else {
          // Fallback: try to find a meaningful string representation
          // But avoid JSON.stringify for objects that might have circular references
          try {
            processedValue = JSON.stringify(value);
          } catch (jsonError) {
            // If JSON.stringify fails (circular reference), use a safer approach
            console.warn(`Field ${fieldId} contains object with circular reference, using fallback:`, jsonError.message);
            processedValue = String(value.toString ? value.toString() : value);
          }
          console.warn(`Field ${fieldId} contains object without value property:`, typeof value);
        }
      } else {
        // Handle primitive values
        processedValue = String(value);
      }


      return {
        fieldId: parseInt(fieldId, 10), // Convert string fieldId to number
        value: processedValue
      };
    });

    // Prepare submission payload with new format
    const payload = {
      formId: formIdString,
      fieldSubmissions,
      // submitterInfo: "" // Don't send submitterInfo for now as requested
    };


    const response = await axiosInstance.post('/api/FormDesk/SubmitForm', payload, {
      headers
    });


    return {
      success: true,
      data: response.data.data || response.data,
      message: response.data.message || 'Form submitted successfully'
    };
  } catch (error) {
    console.error('Error submitting form via webhook:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to submit form',
      error: error.response?.data || error
    };
  }
};

// Transform API form data to preview/public format (matches FormsTable preview transformation)
export const transformAPIFormToPreviewFormat = (fullForm, getHtmlInputType) => {
  return {
    id: fullForm.FormId, // Add id property for compatibility
    name: fullForm.Title,
    title: fullForm.Title,
    description: fullForm.Description,
    formId: fullForm.FormId,
    styling: {
      primaryColor: "#3B82F6",
      backgroundColor: "#FFFFFF",
      textColor: "#1F2937",
      borderRadius: "8px",
      ...(fullForm.Styles ? JSON.parse(fullForm.Styles) : (fullForm.Style ? JSON.parse(fullForm.Style) : {}))
    },
    // Determine if multistep based on steps count
    isMultistep: fullForm.Steps && fullForm.Steps.length > 1,
    // Transform steps and fields structure
    steps: fullForm.Steps ? fullForm.Steps.map(step => ({
      stepId: step.StepId,
      stepTitle: step.StepTitle,
      stepDescription: step.StepDescription,
      stepOrder: step.StepOrder,
      fields: step.Fields ? step.Fields.map(field => ({
        fieldId: field.FieldId,
        id: field.FieldId,
        label: field.FieldLabel,
        // Map FieldTypeId to HTML input type
        type: getHtmlInputType ? getHtmlInputType(field.FieldTypeId) : getFieldTypeHtml(field.FieldTypeId),
        required: field.IsRequired,
        placeholder: field.Placeholder || '',
        defaultValue: field.DefaultValue || '',
        IsMendatory: field.IsMendatory || false, // Preserve IsMendatory from API
        isMandatory: field.IsMendatory || false, // Also set lowercase version for consistency
        masterFieldId: field.MasterFieldId || null, // Include MasterFieldId for phone verification
        options: field.Options ? field.Options.map(option => ({
          id: option.OptionId,
          label: option.OptionLabel,
          value: option.OptionValue
        })) : []
      })) : []
    })) : [],
    // For single-step forms, put fields directly
    fields: (fullForm.Steps && fullForm.Steps.length === 1 && fullForm.Steps[0].Fields)
      ? fullForm.Steps[0].Fields.map(field => ({
        id: field.FieldId,
        label: field.FieldLabel,
        // Map FieldTypeId to HTML input type
        type: getHtmlInputType ? getHtmlInputType(field.FieldTypeId) : getFieldTypeHtml(field.FieldTypeId),
        required: field.IsRequired,
        placeholder: field.Placeholder || '',
        defaultValue: field.DefaultValue || '',
        IsMendatory: field.IsMendatory || false, // Preserve IsMendatory from API
        isMandatory: field.IsMendatory || false, // Also set lowercase version for consistency
        masterFieldId: field.MasterFieldId || null, // Include MasterFieldId for phone verification
        options: field.Options ? field.Options.map(option => ({
          id: option.OptionId,
          label: option.OptionLabel,
          value: option.OptionValue
        })) : []
      }))
      : [],
    apiData: fullForm
  };
};

// Helper function to map field type IDs to HTML types (same as FormPage)
const getFieldTypeHtml = (fieldTypeId) => {
  const fieldTypeMap = {
    1: 'text',     // TextBox
    2: 'textarea', // TextArea
    3: 'number',   // Number
    4: 'date',     // Date
    5: 'checkbox', // Checkbox
    6: 'radio',    // Radio
    7: 'select',   // Dropdown
    8: 'email',    // Email
    9: 'password'  // Password
  };
  return fieldTypeMap[fieldTypeId] || 'text';
};

/**
 * Send OTP to phone number for verification
 * Uses GET /api/Account/SendResendInitialPhoneOTP?OtpId=&Phone=xxx
 * @param {string} phoneNumber - The phone number to send OTP to
 * @param {string} otpId - Optional OtpId for resending OTP
 * @returns {Promise<Object>} - Response with OTP send status and OtpId
 */
export const sendPhoneOTP = async (phoneNumber, otpId = '') => {
  try {
    // Validate phone number format (10 digits)
    const cleanedPhone = phoneNumber.replace(/\D/g, '');
    if (cleanedPhone.length !== 10) {
      return {
        success: false,
        message: 'Please enter a valid 10-digit phone number'
      };
    }

    const response = await axiosInstance.get('/api/Account/SendResendInitialPhoneOTP', {
      params: {
        OtpId: otpId || '',
        Phone: cleanedPhone
      }
    });

    if (response.data?.statusCode >= 400 || response.data?.status === 'Error') {
      return {
        success: false,
        message: response.data?.message || 'Failed to send OTP'
      };
    }

    // Parse the OtpId from the data string
    let parsedOtpId = null;
    if (response.data?.data) {
      try {
        const dataObj = typeof response.data.data === 'string' 
          ? JSON.parse(response.data.data) 
          : response.data.data;
        parsedOtpId = dataObj.OtpId;
      } catch (e) {
        console.warn('Failed to parse OtpId from response:', e);
      }
    }

    return {
      success: true,
      message: response.data?.message || 'OTP sent successfully',
      otpId: parsedOtpId,
      data: response.data?.data
    };
  } catch (error) {
    console.error('Error sending phone OTP:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to send OTP'
    };
  }
};

/**
 * Verify OTP for phone number
 * Uses GET /api/Account/VerifyInitialPhoneOtp?OtpId=xxx&code=xxx
 * @param {string} otpId - The OtpId received from sendPhoneOTP
 * @param {string} code - The OTP code entered by user
 * @returns {Promise<Object>} - Response with verification status
 */
export const verifyPhoneOTP = async (otpId, code) => {
  try {
    if (!otpId) {
      return {
        success: false,
        message: 'Invalid verification session. Please request a new OTP.'
      };
    }

    if (!code || code.length < 4) {
      return {
        success: false,
        message: 'Please enter a valid OTP'
      };
    }

    const response = await axiosInstance.get('/api/Account/VerifyInitialPhoneOtp', {
      params: {
        OtpId: otpId,
        code: code
      }
    });

    // Check for failure status - API returns status: "Failure" for wrong OTP
    if (response.data?.statusCode >= 400 || response.data?.status === 'Error' || response.data?.status === 'Failure') {
      return {
        success: false,
        message: response.data?.message || 'Invalid OTP'
      };
    }

    return {
      success: true,
      message: response.data?.message || 'Phone number verified successfully',
      data: response.data?.data
    };
  } catch (error) {
    console.error('Error verifying phone OTP:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to verify OTP'
    };
  }
};

/**
 * Bulk delete forms
 * @param {string[]} formIds - Array of form IDs (GUIDs)
 * @returns {Promise<Object>} - Response from API
 */
export const bulkDeleteForms = async (formIds) => {
  try {
    if (!formIds || !Array.isArray(formIds) || formIds.length === 0) {
      return {
        success: false,
        message: 'No form IDs provided for deletion'
      };
    }

    // Validate all form IDs are valid GUIDs
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const validFormIds = formIds.filter(id => {
      const idString = String(id).trim();
      return guidRegex.test(idString);
    });

    if (validFormIds.length === 0) {
      return {
        success: false,
        message: 'No valid form IDs provided for deletion'
      };
    }

    const payload = {
      formIds: validFormIds
    };

    const response = await axiosInstance.delete('/api/FormDesk/BulkDeleteForms', {
      data: payload
    });

    // Check if API returned a failure status
    if (response.data?.status === "Failure" || response.data?.status === "failure" ||
        (response.data?.statusCode && response.data.statusCode >= 400)) {
      return {
        success: false,
        message: response.data?.message || 'Failed to delete forms'
      };
    }

    return {
      success: true,
      message: response.data?.message || `${validFormIds.length} form(s) deleted successfully`
    };
  } catch (error) {
    console.error('Error bulk deleting forms:', error);

    let errorMessage = 'Failed to delete forms';
    if (error.response) {
      const serverMessage = error.response.data?.message || error.response.data?.error;
      errorMessage = serverMessage || errorMessage;
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      message: errorMessage,
      error: true
    };
  }
};

export default {
  getFormCategories,
  getFieldTypes,
  saveForm,
  createForm,
  updateForm,
  transformAPIFormToBuilderFormat,
  transformAPIFormToPreviewFormat,
  getFormById,
  getAllForms,
  getForms,
  deleteForm,
  bulkDeleteForms,
  updateFormWebsites,
  updateFormStatus,
  getFormByIdWebhook,
  submitFormWebhook,
  sendPhoneOTP,
  verifyPhoneOTP
};
